/**
 * Centralized error handling utilities
 * Provides consistent error handling across the application
 */

// Error types for categorization
export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION',
  PERMISSION = 'PERMISSION',
  VALIDATION = 'VALIDATION',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  NOT_FOUND = 'NOT_FOUND',
  TIMEOUT = 'TIMEOUT',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN'
}

// Structure for enhanced error information
export interface EnhancedError extends Error {
  code?: string;
  status?: number;
  type?: ErrorType;
  details?: string;
  context?: string;
  isRetryable?: boolean;
}

/**
 * Categorize errors by type for consistent handling
 */
export function categorizeError(error: any): ErrorType {
  if (!error) return ErrorType.UNKNOWN;

  const rawCode = String(error.code || "");
  if (
    rawCode === "QUOTA_CIRCUIT_BREAKER" ||
    rawCode === "API_COOLDOWN" ||
    rawCode === "API_SESSION_HARD_LOCK"
  ) {
    return ErrorType.QUOTA_EXCEEDED;
  }
  if (rawCode === "PAYLOAD_TOO_LARGE") {
    return ErrorType.VALIDATION;
  }

  const message = (error.message || '').toLowerCase();
  const code = rawCode.toLowerCase();
  const status = error.status || 0;
  
  // Network errors
  if (
    message.includes('network') || 
    message.includes('offline') ||
    message.includes('internet') ||
    message.includes('connection') ||
    error.name === 'AbortError' ||
    code.includes('network')
  ) {
    return ErrorType.NETWORK;
  }
  
  // Authentication errors
  if (
    status === 401 ||
    message.includes('unauthenticated') ||
    message.includes('not authenticated') ||
    message.includes('auth') ||
    message.includes('login') ||
    message.includes('sign in') ||
    code.includes('auth/')
  ) {
    return ErrorType.AUTHENTICATION;
  }
  
  // Permission errors
  if (
    status === 403 ||
    message.includes('permission') ||
    message.includes('access denied') ||
    message.includes('not allowed') ||
    message.includes('forbidden') ||
    message.includes('insufficient') ||
    code.includes('permission-denied')
  ) {
    return ErrorType.PERMISSION;
  }
  
  // Validation errors
  if (
    status === 400 ||
    message.includes('invalid') ||
    message.includes('validation') ||
    message.includes('required')
  ) {
    return ErrorType.VALIDATION;
  }
  
  // Quota exceeded
  if (
    message.includes('quota') ||
    message.includes('rate limit') ||
    message.includes('too many requests') ||
    status === 429 ||
    code.includes('resource-exhausted')
  ) {
    return ErrorType.QUOTA_EXCEEDED;
  }
  
  // Not found
  if (
    status === 404 ||
    message.includes('not found') ||
    message.includes('missing') ||
    code.includes('not-found')
  ) {
    return ErrorType.NOT_FOUND;
  }
  
  // Timeout
  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    code === 'TIMEOUT' ||
    error.name === 'TimeoutError'
  ) {
    return ErrorType.TIMEOUT;
  }
  
  // Server errors
  if (
    status >= 500 ||
    message.includes('server') ||
    message.includes('internal') ||
    message.includes('5xx') ||
    message.includes('rst_stream')
  ) {
    return ErrorType.SERVER;
  }
  
  return ErrorType.UNKNOWN;
}

/**
 * Enhance an error with additional context and type
 */
export function enhanceError(
  error: any, 
  context: string, 
  additionalDetails?: string
): EnhancedError {
  const enhancedError: EnhancedError = error instanceof Error 
    ? error 
    : new Error(typeof error === 'string' ? error : 'Unknown error');
  
  enhancedError.context = context;
  enhancedError.type = categorizeError(error);
  
  // Preserve original properties
  if (error) {
    if (error.code) enhancedError.code = error.code;
    if (error.status) enhancedError.status = error.status;
    if (error.details) enhancedError.details = error.details;
  }
  
  // Add additional details if provided
  if (additionalDetails) {
    enhancedError.details = enhancedError.details 
      ? `${enhancedError.details}; ${additionalDetails}`
      : additionalDetails;
  }
  
  // Determine if error is retryable
  enhancedError.isRetryable = [
    ErrorType.NETWORK,
    ErrorType.TIMEOUT,
    ErrorType.SERVER,
    ErrorType.QUOTA_EXCEEDED
  ].includes(enhancedError.type as ErrorType);

  if (
    enhancedError.code === "QUOTA_CIRCUIT_BREAKER" ||
    enhancedError.code === "API_COOLDOWN" ||
    enhancedError.code === "API_SESSION_HARD_LOCK" ||
    enhancedError.code === "PAYLOAD_TOO_LARGE"
  ) {
    enhancedError.isRetryable = false;
  }

  return enhancedError;
}

/**
 * Log an error with consistent formatting
 */
export function logError(
  error: any, 
  context: string, 
  additionalDetails?: string
): EnhancedError {
  const enhanced = enhanceError(error, context, additionalDetails);
  
  console.error(
    `[ERROR] ${context}: ${enhanced.message}`,
    {
      type: enhanced.type,
      code: enhanced.code,
      status: enhanced.status,
      details: enhanced.details,
      stack: enhanced.stack,
      isRetryable: enhanced.isRetryable
    }
  );
  
  return enhanced;
}

/**
 * Get a user-friendly error message
 */
export function getUserFriendlyMessage(error: any): string {
  const enhanced = error.type ? error : enhanceError(error, 'unknown');
  const code = String(enhanced.code || '').toUpperCase();

  // Explicit environment/secret mismatch guidance for admin API calls.
  if (code === "AUTH_SECRET_MISMATCH" || code === "AUTH_INVALID" || code === "AUTH_REQUIRED") {
    return "Sign in again. API calls require a valid Firebase session or staff access token.";
  }
  if (code === "ENV_CONFIG_ERROR") {
    return "Server configuration error: ADMIN_SECRET is missing in Netlify environment variables.";
  }
  if (code === "QUOTA_CIRCUIT_BREAKER") {
    return "Too many requests were paused to protect your quota. Please wait about a minute and try again.";
  }
  if (code === "API_COOLDOWN") {
    return "The server asked us to slow down. Please wait a moment before retrying.";
  }
  if (code === "API_SESSION_HARD_LOCK") {
    return "Quota protection locked this session after repeated overloads. Refresh the page to continue.";
  }
  if (code === "PAYLOAD_TOO_LARGE") {
    return enhanced.message || "Payload too large. Shorten notes or other text and try again.";
  }
  if (code === "SLOT_UNAVAILABLE") {
    return (
      enhanced.message ||
      "That time slot is full or unavailable. Try another time."
    );
  }
  if (code === "DATE_BLOCKED") {
    return "This date is not available for booking. Please pick another day.";
  }
  if (code === "BOOKING_ARCHIVED") {
    return "This visit is archived. Restore it before recording a payment.";
  }
  if (code === "BOOKING_NOT_FOUND") {
    return "That booking could not be found. Refresh and try again.";
  }

  switch (enhanced.type) {
    case ErrorType.NETWORK:
      return 'Network connection issue. Please check your internet and try again.';
    
    case ErrorType.AUTHENTICATION:
      return 'Your session has expired. Please sign in again.';
    
    case ErrorType.PERMISSION:
      return 'You don\'t have permission to perform this action.';
    
    case ErrorType.VALIDATION:
      return enhanced.message || 'Please check your input and try again.';
    
    case ErrorType.QUOTA_EXCEEDED:
      return 'Service temporarily unavailable due to high demand. Please try again later.';
    
    case ErrorType.NOT_FOUND:
      return 'The requested information could not be found.';
    
    case ErrorType.TIMEOUT:
      return 'The operation timed out. Please try again.';
    
    case ErrorType.SERVER:
      return 'Server error. Our team has been notified and is working on it.';
    
    default:
      return enhanced.message || 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Handle an error with logging and return a user-friendly message
 */
export function handleError(
  error: any, 
  context: string, 
  additionalDetails?: string
): string {
  const enhanced = logError(error, context, additionalDetails);
  return getUserFriendlyMessage(enhanced);
}